import React, { useState, useEffect } from 'react';
import { landwirtschaftShopId } from '../config';
import {
  createCart,
  fetchCart,
  addToCart,
  removeFromCart,
  updateCartLines,
  transformCartToCheckoutFormat,
} from '../services/cartService';

import Context from '../context/StoreContext';
import fetchClubs from './fetchClubs';

const ContextProvider = ({ children }) => {
  let initialStoreState = {
    clubId: undefined,
    clubName: '',
    clubs: [],
    adding: false,
    checkout: { lineItems: [] },
    cart: null,
    products: [],
    shop: {},
    theme: 'normal',
  };

  const [store, updateStore] = useState(initialStoreState);

  /** get selected club from localStorage */
  useEffect(() => {
    const clubId = localStorage.getItem('club_id');
    const clubName = localStorage.getItem('club_name');
    const theme = clubId == landwirtschaftShopId ? 'edeka' : 'normal';

    updateStore(prevState => ({
      ...prevState,
      clubId,
      clubName,
      theme,
    }));
  }, [store.clubs]);

  useEffect(() => {
    const initializeCart = async () => {
      // Check for an existing cart.
      const isBrowser = typeof window !== 'undefined';
      const existingCartID = isBrowser
        ? localStorage.getItem('shopify_cart_id')
        : null;

      const setCartInState = cart => {
        if (isBrowser) {
          localStorage.setItem('shopify_cart_id', cart.id);
        }

        // Transform cart to checkout format for backward compatibility
        const checkout = transformCartToCheckoutFormat(cart);

        updateStore(prevState => {
          return { ...prevState, cart, checkout };
        });
      };

      if (existingCartID) {
        try {
          const cart = await fetchCart(existingCartID);

          // If cart exists and is still valid, use it
          if (cart) {
            setCartInState(cart);
            return;
          }
        } catch (e) {
          console.error('Error fetching cart:', e);
          localStorage.removeItem('shopify_cart_id');
        }
      }

      // Create a new cart if no valid cart exists
      try {
        const newCart = await createCart();
        setCartInState(newCart);
      } catch (e) {
        console.error('Error creating cart:', e);
      }
    };

    initializeCart();
  }, []);

  /** fetch clubs list on load */
  useEffect(() => {
    fetchClubs()
      .then(res => res.json())
      .then(clubs => {
        updateStore(prevState => ({ ...prevState, clubs }));
      });
  }, []);

  return (
    <Context.Provider
      value={{
        store,
        setClub: clubId => {
          const club = store.clubs.find(({ id }) => id === clubId);
          const theme = clubId === 548 ? 'edeka' : 'normal';

          updateStore(prevState => ({
            ...prevState,
            clubId,
            clubName: club.title,
            theme,
          }));
          localStorage.setItem('club_id', clubId);
          localStorage.setItem('club_name', club.title);
        },
        resetClub: () => {
          updateStore(prevState => ({
            ...prevState,
            clubId: undefined,
            clubName: '',
            theme: 'normal',
          }));
          localStorage.removeItem('club_id');
          localStorage.removeItem('club_name');
        },
        addVariantToCart: async (variantId, quantity, customAttributes) => {
          if (variantId === '' || !quantity) {
            console.error('Both a size and quantity are required.');
            return;
          }

          updateStore(prevState => {
            return { ...prevState, adding: true };
          });

          const { cart } = store;

          if (!cart) {
            console.error('Cart not initialized');
            updateStore(prevState => ({ ...prevState, adding: false }));
            return;
          }

          try {
            // Transform custom attributes to the format expected by the Cart API
            const attributes = customAttributes || [];
            
            const lines = [{
              merchandiseId: variantId,
              quantity: parseInt(quantity, 10),
              attributes,
            }];

            const updatedCart = await addToCart(cart.id, lines);
            const checkout = transformCartToCheckoutFormat(updatedCart);

            updateStore(prevState => {
              return { ...prevState, cart: updatedCart, checkout, adding: false };
            });
          } catch (error) {
            console.error('Error adding to cart:', error);
            updateStore(prevState => ({ ...prevState, adding: false }));
          }
        },
        removeLineItem: async (client, checkoutID, lineItemID) => {
          const { cart } = store;

          if (!cart) {
            console.error('Cart not initialized');
            return;
          }

          try {
            const updatedCart = await removeFromCart(cart.id, [lineItemID]);
            const checkout = transformCartToCheckoutFormat(updatedCart);

            updateStore(prevState => {
              return { ...prevState, cart: updatedCart, checkout };
            });
          } catch (error) {
            console.error('Error removing from cart:', error);
          }
        },
        updateLineItem: async (client, checkoutID, lineItemID, quantity) => {
          const { cart } = store;

          if (!cart) {
            console.error('Cart not initialized');
            return;
          }

          try {
            const lines = [{
              id: lineItemID,
              quantity: parseInt(quantity, 10),
            }];

            const updatedCart = await updateCartLines(cart.id, lines);
            const checkout = transformCartToCheckoutFormat(updatedCart);

            updateStore(prevState => {
              return { ...prevState, cart: updatedCart, checkout };
            });
          } catch (error) {
            console.error('Error updating cart:', error);
          }
        },
        // Keep the client property for backward compatibility, but it's no longer used
        client: null,
      }}
    >
      {children}
    </Context.Provider>
  );
};
export default ContextProvider;
